import React from "react";

import { CgArrowRight } from "react-icons/cg";

import { Button } from "components/button";
import { Card } from "components/card";
import { Heading } from "components/heading";
import { PageServerParams as CohortPageParams } from "pages/programs/[programSlug]/cohorts/[cohortSlug]";
import { Application } from "types/application.type";
import { isApplicationSubmitted } from "utils/application-state";
import { getReviewUrl, getStepUrl } from "utils/url";

interface ContinueApplicationCardProps extends Omit<CohortPageParams, "applySteps"> {
  application: Application;
  isComplete: boolean;
  firstUncompletedStep?: CohortPageParams["applySteps"][number];
}

export const ContinueApplicationCard: React.FC<React.PropsWithChildren<Readonly<ContinueApplicationCardProps>>> = ({
  application,
  cohort,
  program,
  isComplete,
  firstUncompletedStep,
}) => {
  const isSubmitted = isApplicationSubmitted(application);

  let buttonText = "Continue application";
  if (isSubmitted) {
    buttonText = "Edit application";
  } else if (isComplete) {
    buttonText = "Review application";
  }

  return (
    <Card className="max-w-xl m-auto mb-8 rounded-lg shadow-lg">
      <Card.Header>
        <Heading
          size={3}
          semanticSize={2}
          text={isSubmitted ? "Application submitted ✅" : "Continue your application ✍️"}
        />
      </Card.Header>
      <Card.Body>
        <p>
          {isSubmitted ? (
            "Your application is awaiting review by the program director. You can make edits while it is awaiting review."
          ) : (
            <>
              Pick up from where you left off
              {firstUncompletedStep?.title ? ` in the ${firstUncompletedStep?.title} section` : ``}. We will continue to
              save your application as you progress.
            </>
          )}
        </p>
        <Button
          className="mt-4"
          size="sm"
          eventName="continueButtonClick"
          link={
            isComplete
              ? getReviewUrl(program.slug, cohort.slug)
              : getStepUrl(program.slug, cohort.slug, firstUncompletedStep?.slug)
          }
          internalLink
          label={
            <>
              {buttonText}
              <CgArrowRight className="inline w-4 h-4 text-white" />
            </>
          }
        />
      </Card.Body>
    </Card>
  );
};
